import type { StateCreator } from 'zustand'
import type { Lote } from '../lib/operacoesEmMassa'

// Client-side overlay for IFCEdit. Mirrors the BoQ pattern: every
// edit lives in the user's browser, the server only sees audit
// pings. This keeps cost near zero and 300+ concurrent users
// feasible on the same Render free instance.
//
// Three overlays:
//   * attributeEdits[guid][attrName]: string | null. Pending
//     attribute edits (Name, Description, ObjectType, Tag,
//     LongName, PredefinedType). Mapped to STEP arg slots at
//     export time by lib/stepPatch.applyAttributeEdits.
//   * classChanges[guid]: string. Pending IFC class change for
//     an entity. Source + target classes are restricted to the
//     compatible-class set in lib/classChange.ts. Applied BEFORE
//     attribute edits at export time so any subsequent
//     PredefinedType edit lands on the right enum.
//   * psetEdits[guid][psetName][propName]: string | null. Pending
//     property/quantity value edits inside existing pset/qset
//     entities (IfcPropertySingleValue.NominalValue or
//     IfcQuantity*.<Value>). Adding new psets / properties is NOT
//     covered yet.

export interface IfcEditSlice {
  /** Map<guid, Map<attribute, value | null>>. */
  ifcEditAttributeEdits: Record<string, Record<string, string | null>>
  /** Map<guid, newClassName>. */
  ifcEditClassChanges: Record<string, string>
  /** Map<guid, Map<psetName, Map<propName, value | null>>>. */
  ifcEditPsetEdits: Record<
    string,
    Record<string, Record<string, string | null>>
  >
  /** Counter bumped on every overlay mutation so the panel
   *  re-renders when the user types in another popup window. */
  ifcEditVersion: number

  setIfcEditAttribute: (
    guid: string,
    attribute: string,
    value: string | null,
  ) => void
  clearIfcEditAttribute: (guid: string, attribute: string) => void
  clearIfcEditElement: (guid: string) => void
  setIfcEditClass: (guid: string, newClass: string) => void
  clearIfcEditClass: (guid: string) => void
  setIfcEditPset: (
    guid: string,
    psetName: string,
    propName: string,
    value: string | null,
  ) => void
  clearIfcEditPset: (guid: string, psetName: string, propName: string) => void
  resetIfcEdit: () => void
  /** Operações em massa (importar de Excel, renomear, apagar), por
   *  ordem. Cada lote anula-se de uma vez. Aplicam-se ao gravar, depois
   *  das edições feitas no painel de propriedades. */
  ifcEditLotes: Lote[]
  /** Lotes anulados, para refazer. Limpa-se quando entra um lote novo. */
  ifcEditLotesAnulados: Lote[]
  acrescentarLote: (lote: Lote) => void
  anularLote: () => void
  refazerLote: () => void
}

export const createIfcEditSlice: StateCreator<IfcEditSlice> = (set) => ({
  ifcEditAttributeEdits: {},
  ifcEditClassChanges: {},
  ifcEditPsetEdits: {},
  ifcEditLotes: [],
  ifcEditLotesAnulados: [],
  acrescentarLote: (lote) =>
    set((s) => ({
      ifcEditLotes: [...s.ifcEditLotes, lote],
      ifcEditLotesAnulados: [],
      ifcEditVersion: s.ifcEditVersion + 1,
    })),
  anularLote: () =>
    set((s) => {
      const ultimo = s.ifcEditLotes[s.ifcEditLotes.length - 1]
      if (!ultimo) return {}
      return {
        ifcEditLotes: s.ifcEditLotes.slice(0, -1),
        ifcEditLotesAnulados: [...s.ifcEditLotesAnulados, ultimo],
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),
  refazerLote: () =>
    set((s) => {
      const ultimo = s.ifcEditLotesAnulados[s.ifcEditLotesAnulados.length - 1]
      if (!ultimo) return {}
      return {
        ifcEditLotes: [...s.ifcEditLotes, ultimo],
        ifcEditLotesAnulados: s.ifcEditLotesAnulados.slice(0, -1),
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),
  ifcEditVersion: 0,

  setIfcEditAttribute: (guid, attribute, value) =>
    set((s) => {
      const prev = s.ifcEditAttributeEdits[guid] ?? {}
      const nextForGuid = { ...prev, [attribute]: value }
      return {
        ifcEditAttributeEdits: {
          ...s.ifcEditAttributeEdits,
          [guid]: nextForGuid,
        },
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),

  clearIfcEditAttribute: (guid, attribute) =>
    set((s) => {
      const prev = s.ifcEditAttributeEdits[guid]
      if (!prev || !(attribute in prev)) return s
      const nextForGuid = { ...prev }
      delete nextForGuid[attribute]
      const nextAll = { ...s.ifcEditAttributeEdits }
      if (Object.keys(nextForGuid).length === 0) {
        delete nextAll[guid]
      } else {
        nextAll[guid] = nextForGuid
      }
      return {
        ifcEditAttributeEdits: nextAll,
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),

  clearIfcEditElement: (guid) =>
    set((s) => {
      const hasAttrs = guid in s.ifcEditAttributeEdits
      const hasClass = guid in s.ifcEditClassChanges
      const hasPsets = guid in s.ifcEditPsetEdits
      if (!hasAttrs && !hasClass && !hasPsets) return s
      const nextAttrs = { ...s.ifcEditAttributeEdits }
      delete nextAttrs[guid]
      const nextClasses = { ...s.ifcEditClassChanges }
      delete nextClasses[guid]
      const nextPsets = { ...s.ifcEditPsetEdits }
      delete nextPsets[guid]
      return {
        ifcEditAttributeEdits: nextAttrs,
        ifcEditClassChanges: nextClasses,
        ifcEditPsetEdits: nextPsets,
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),

  setIfcEditClass: (guid, newClass) =>
    set((s) => ({
      ifcEditClassChanges: { ...s.ifcEditClassChanges, [guid]: newClass },
      ifcEditVersion: s.ifcEditVersion + 1,
    })),

  clearIfcEditClass: (guid) =>
    set((s) => {
      if (!(guid in s.ifcEditClassChanges)) return s
      const next = { ...s.ifcEditClassChanges }
      delete next[guid]
      return {
        ifcEditClassChanges: next,
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),

  setIfcEditPset: (guid, psetName, propName, value) =>
    set((s) => {
      const prevForGuid = s.ifcEditPsetEdits[guid] ?? {}
      const prevForPset = prevForGuid[psetName] ?? {}
      const nextForPset = { ...prevForPset, [propName]: value }
      const nextForGuid = { ...prevForGuid, [psetName]: nextForPset }
      return {
        ifcEditPsetEdits: {
          ...s.ifcEditPsetEdits,
          [guid]: nextForGuid,
        },
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),

  clearIfcEditPset: (guid, psetName, propName) =>
    set((s) => {
      const forGuid = s.ifcEditPsetEdits[guid]
      if (!forGuid) return s
      const forPset = forGuid[psetName]
      if (!forPset || !(propName in forPset)) return s
      const nextForPset = { ...forPset }
      delete nextForPset[propName]
      const nextForGuid = { ...forGuid }
      if (Object.keys(nextForPset).length === 0) {
        delete nextForGuid[psetName]
      } else {
        nextForGuid[psetName] = nextForPset
      }
      const nextAll = { ...s.ifcEditPsetEdits }
      if (Object.keys(nextForGuid).length === 0) {
        delete nextAll[guid]
      } else {
        nextAll[guid] = nextForGuid
      }
      return {
        ifcEditPsetEdits: nextAll,
        ifcEditVersion: s.ifcEditVersion + 1,
      }
    }),

  resetIfcEdit: () =>
    set({
      ifcEditAttributeEdits: {},
      ifcEditClassChanges: {},
      ifcEditPsetEdits: {},
      ifcEditLotes: [],
      ifcEditLotesAnulados: [],
      ifcEditVersion: 0,
    }),
})
